import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { SKUSetting } from "@/lib/types";
import { createFileRoute, Link } from "@tanstack/react-router";
import { inventoryPath, users_sku_settings_route } from "@/constants";
import { Button } from "@/components/ui/button";
import { authedFetch } from "@/lib/api-client";
import { getOrgIdWithFallback } from "@/lib/utils";
import { useAuth } from "@/providers/auth";
import { useSkuGenerator } from "@/hooks/use-sku-generator";
import { useForm, type SubmitHandler } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod/v4";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import { isGated } from "@/hooks/gated";
import GatedOverlay from "@/components/ui/gated-overlay";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n";

export const Route = createFileRoute("/$org/settings/sku-generator")({
  component: RouteComponent,
  loader: async ({ context }) => {
    const { getToken, setToken, handleTokenInvalid } = context.auth;
    const orgId = getOrgIdWithFallback(context.org.orgId);
    const gated = await isGated(
      {
        token: getToken() as string,
        setToken,
        orgId: orgId,
      },
      handleTokenInvalid,
    );
    try {
      const resp = await authedFetch(users_sku_settings_route, {
        method: "GET",
        token: getToken() as string,
        orgId: orgId,
      });

      const token = resp.headers.get("access-token");
      if (token) {
        setToken(token);
      }

      const skuSetting = (await resp.json()) as SKUSetting;

      return {
        skuSetting,
        gated: gated?.is_gated,
      };
    } catch (e) {
      console.error(e);
      return {
        skuSetting: undefined,
        gated: gated?.is_gated,
      };
    }
  },
});

const formSchema = z.object({
  id: z.string(),
  prefix: z
    .string()
    .max(4, { error: () => i18n.t("skuGenerator.validation.maxLength") })
    .refine((val) => val === "" || /^[a-zA-Z0-9]+$/.test(val), {
      error: () => i18n.t("skuGenerator.validation.alphanumeric"),
    })
    .optional(),
  suffix: z
    .string()
    .max(4, { error: () => i18n.t("skuGenerator.validation.maxLength") })
    .refine((val) => val === "" || /^[a-zA-Z0-9]+$/.test(val), {
      error: () => i18n.t("skuGenerator.validation.alphanumeric"),
    })
    .optional(),
  delimiter: z.enum(["", "-", "_", ".", "null"]),
  autoCreate: z.boolean(),
  generationStrategy: z
    .enum(["RANDOM", "PRODUCT_BASED", "TEMPLATE"])
    .optional(),
  skuTemplate: z.string().optional(),
  skuLength: z.number().min(1).max(50).optional(),
  characterSet: z
    .enum(["ALPHANUMERIC", "ALPHABETIC", "NUMERIC", "CUSTOM"])
    .optional(),
  customCharacters: z.string().optional(),
  maxRetries: z.number().min(1).max(10).optional(),
});

type formSchemaType = z.infer<typeof formSchema>;

function RouteComponent() {
  const { t } = useTranslation();
  const { gated } = Route.useLoaderData();
  const { org } = Route.useParams();
  const [showAdvanced, setShowAdvanced] = useState(false);

  const { user } = useAuth();
  const mutation = useSkuGenerator();
  const { skuSetting } = Route.useLoaderData();

  const form = useForm<formSchemaType>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      id: skuSetting?.id || "",
      autoCreate: skuSetting?.auto_create ?? false,
      delimiter: (skuSetting?.delimiter === null
        ? "null"
        : skuSetting?.delimiter || "null") as "" | "." | "_" | "-" | "null",
      prefix: skuSetting?.prefix || "",
      suffix: skuSetting?.suffix || "",
      generationStrategy: skuSetting?.generation_strategy || "RANDOM",
      skuTemplate: skuSetting?.sku_template || "",
      skuLength: skuSetting?.sku_length || 12,
      characterSet: skuSetting?.character_set || "ALPHANUMERIC",
      customCharacters: skuSetting?.custom_characters || "",
      maxRetries: skuSetting?.max_retries || 3,
    },
  });

  const invenPath = `/${user.account?.orgs[0].name}${inventoryPath}`;

  const delimiterOptions = [
    { label: t("skuGenerator.delimiters.hyphen"), value: "-" },
    { label: t("skuGenerator.delimiters.underscore"), value: "_" },
    { label: t("skuGenerator.delimiters.dot"), value: "." },
    { label: t("skuGenerator.delimiters.none"), value: "null" },
  ];

  const generationStrategyOptions = [
    {
      label: t("skuGenerator.strategies.random.label"),
      value: "RANDOM",
      description: t("skuGenerator.strategies.random.description"),
    },
    {
      label: t("skuGenerator.strategies.product.label"),
      value: "PRODUCT_BASED",
      description: t("skuGenerator.strategies.product.description"),
    },
    {
      label: t("skuGenerator.strategies.template.label"),
      value: "TEMPLATE",
      description: t("skuGenerator.strategies.template.description"),
    },
  ];

  const characterSetOptions = [
    {
      label: t("skuGenerator.characterSets.alphanumeric"),
      value: "ALPHANUMERIC",
    },
    { label: t("skuGenerator.characterSets.alphabetic"), value: "ALPHABETIC" },
    { label: t("skuGenerator.characterSets.numeric"), value: "NUMERIC" },
    { label: t("skuGenerator.characterSets.custom"), value: "CUSTOM" },
  ];

  const watchedStrategy = form.watch("generationStrategy") || "RANDOM";
  const watchedCharacterSet = form.watch("characterSet") || "ALPHANUMERIC";

  const onSubmit: SubmitHandler<formSchemaType> = (data) => {
    mutation.mutate({
      id: data.id,
      prefix: data.prefix || "",
      suffix: data.suffix || "",
      auto_create: data.autoCreate,
      delimiter: data.delimiter === "null" ? "" : data.delimiter,
      generation_strategy: data.generationStrategy || "RANDOM",
      sku_template: data.skuTemplate || "",
      sku_length: data.skuLength || 12,
      character_set: data.characterSet || "ALPHANUMERIC",
      custom_characters: data.customCharacters || "",
      max_retries: data.maxRetries || 3,
    });
  };

  if (gated) {
    return <GatedOverlay org={org} />;
  }

  return (
    <div>
      <div className="mx-auto max-w-4xl">
        <div className="space-y-6">
          {/* General Settings */}
          <Card>
            <CardHeader>
              <CardTitle>{t("skuGenerator.generalTitle")}</CardTitle>
              <CardDescription>
                {t("skuGenerator.generalDescription")}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit(onSubmit)}
                  className="space-y-6"
                >
                  <FormField
                    control={form.control}
                    name="id"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Input type="hidden" {...field} />
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="autoCreate"
                    render={({ field }) => (
                      <FormItem className="flex items-center space-x-2">
                        <FormControl>
                          <Checkbox
                            id="autoCreate"
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <FormLabel>
                          {t("skuGenerator.autoCreate")}
                        </FormLabel>
                      </FormItem>
                    )}
                  />

                  {/* Generation Strategy */}
                  <div className="space-y-4">
                    <Label>{t("skuGenerator.strategy")}</Label>
                    <FormField
                      control={form.control}
                      name="generationStrategy"
                      render={({ field }) => (
                        <FormItem>
                          <Select
                            onValueChange={field.onChange}
                            defaultValue={field.value}
                          >
                            <SelectTrigger className="w-32">
                              <SelectValue
                                placeholder={t("skuGenerator.strategyPlaceholder")}
                              >
                                {field.value &&
                                  generationStrategyOptions.find(
                                    (opt) => opt.value === field.value,
                                  )?.label}
                              </SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                              <SelectGroup>
                                {generationStrategyOptions.map((opt) => (
                                  <SelectItem key={opt.value} value={opt.value}>
                                    <div>
                                      <div className="font-medium">
                                        {opt.label}
                                      </div>
                                      <div className="text-xs text-muted-foreground">
                                        {opt.description}
                                      </div>
                                    </div>
                                  </SelectItem>
                                ))}
                              </SelectGroup>
                            </SelectContent>
                          </Select>
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* Template Configuration (only show for TEMPLATE strategy) */}
                  {watchedStrategy === "TEMPLATE" && (
                    <div className="space-y-4 p-4 border rounded-lg bg-muted/50">
                      <Label>{t("skuGenerator.templateConfig")}</Label>
                      <FormField
                        control={form.control}
                        name="skuTemplate"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t("skuGenerator.template")}</FormLabel>
                            <FormControl>
                              <Input
                                {...field}
                                placeholder={t("skuGenerator.templatePlaceholder")}
                                autoComplete="off"
                              />
                            </FormControl>
                            <div className="text-xs text-muted-foreground space-y-1">
                              <div>
                                {t("skuGenerator.templateAvailable", {
                                  placeholders:
                                    "{PREFIX}, {SUFFIX}, {TITLE:N}, {COUNTER:N}, {RANDOM:N}, {OPTION1}",
                                })}
                              </div>
                              <div className="text-xs text-muted-foreground/80">
                                {t("skuGenerator.templateExample")}
                              </div>
                            </div>
                          </FormItem>
                        )}
                      />
                    </div>
                  )}

                  {/* Basic Format Settings */}
                  <div className="space-y-4">
                    <Label className="text-base font-semibold">
                      {t("skuGenerator.basicFormat")}
                    </Label>
                    <div className="grid grid-cols-3 gap-4">
                      <FormField
                        control={form.control}
                        name="prefix"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t("skuGenerator.prefix")}</FormLabel>
                            <FormControl>
                              <Input
                                {...field}
                                maxLength={4}
                                placeholder={t("skuGenerator.optional")}
                                autoComplete="off"
                              />
                            </FormControl>
                            <div className="text-xs text-muted-foreground">
                              {t("skuGenerator.affixHint")}
                            </div>
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="suffix"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t("skuGenerator.suffix")}</FormLabel>
                            <FormControl>
                              <Input
                                {...field}
                                maxLength={4}
                                placeholder={t("skuGenerator.optional")}
                                autoComplete="off"
                              />
                            </FormControl>
                            <div className="text-xs text-muted-foreground">
                              {t("skuGenerator.affixHint")}
                            </div>
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="delimiter"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t("skuGenerator.delimiter")}</FormLabel>
                            <FormControl>
                              <Select
                                onValueChange={field.onChange}
                                defaultValue={field.value}
                              >
                                <SelectTrigger>
                                  <SelectValue
                                    placeholder={t("skuGenerator.delimiters.none")}
                                  />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectGroup>
                                    {delimiterOptions.map((opt, idx) => (
                                      <SelectItem key={idx} value={opt.value}>
                                        {opt.label}
                                      </SelectItem>
                                    ))}
                                  </SelectGroup>
                                </SelectContent>
                              </Select>
                            </FormControl>
                            <div className="text-xs text-muted-foreground">
                              {t("skuGenerator.delimiterHint")}
                            </div>
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>

                  {/* Advanced Settings */}
                  <div className="space-y-4">
                    <button
                      type="button"
                      onClick={() => setShowAdvanced(!showAdvanced)}
                      className="flex items-center space-x-2 text-base font-semibold hover:text-primary transition-colors"
                    >
                      {showAdvanced ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                      <span>{t("skuGenerator.advanced")}</span>
                    </button>

                    {showAdvanced && (
                      <div className="space-y-4 p-4 border rounded-lg bg-muted/50">
                        <p className="text-sm text-muted-foreground">
                          {t("skuGenerator.advancedDescription")}
                        </p>

                        <div className="grid grid-cols-2 gap-4">
                          <FormField
                            control={form.control}
                            name="skuLength"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>{t("skuGenerator.length")}</FormLabel>
                                <FormControl>
                                  <Input
                                    type="number"
                                    {...field}
                                    onChange={(e) =>
                                      field.onChange(
                                        parseInt(e.target.value) || 12,
                                      )
                                    }
                                    min={1}
                                    max={50}
                                  />
                                </FormControl>
                                <div className="text-xs text-muted-foreground">
                                  {t("skuGenerator.lengthHint")}
                                </div>
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={form.control}
                            name="maxRetries"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>{t("skuGenerator.maxRetries")}</FormLabel>
                                <FormControl>
                                  <Input
                                    type="number"
                                    {...field}
                                    onChange={(e) =>
                                      field.onChange(
                                        parseInt(e.target.value) || 3,
                                      )
                                    }
                                    min={1}
                                    max={10}
                                  />
                                </FormControl>
                                <div className="text-xs text-muted-foreground">
                                  {t("skuGenerator.maxRetriesHint")}
                                </div>
                              </FormItem>
                            )}
                          />
                        </div>

                        <FormField
                          control={form.control}
                          name="characterSet"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>{t("skuGenerator.characterSet")}</FormLabel>
                              <FormControl>
                                <Select
                                  onValueChange={field.onChange}
                                  defaultValue={field.value}
                                >
                                  <SelectTrigger>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectGroup>
                                      {characterSetOptions.map((opt) => (
                                        <SelectItem
                                          key={opt.value}
                                          value={opt.value}
                                        >
                                          {opt.label}
                                        </SelectItem>
                                      ))}
                                    </SelectGroup>
                                  </SelectContent>
                                </Select>
                              </FormControl>
                            </FormItem>
                          )}
                        />

                        {watchedCharacterSet === "CUSTOM" && (
                          <FormField
                            control={form.control}
                            name="customCharacters"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>
                                  {t("skuGenerator.customCharacters")}
                                </FormLabel>
                                <FormControl>
                                  <Input
                                    {...field}
                                    placeholder={t("skuGenerator.customCharactersPlaceholder")}
                                    autoComplete="off"
                                  />
                                </FormControl>
                                <div className="text-xs text-muted-foreground">
                                  {t("skuGenerator.customCharactersHint")}
                                </div>
                              </FormItem>
                            )}
                          />
                        )}
                      </div>
                    )}
                  </div>

                  <div className="flex flex-row items-center space-x-2 justify-end">
                    <Link to={invenPath} className="text-sm hover:underline">
                      {t("skuGenerator.generateSkus")}
                    </Link>
                    <Button type="submit">{t("skuGenerator.save")}</Button>
                  </div>
                </form>
              </Form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
